import type { ReactNode } from 'react';
import { Code } from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';

export const TYPE_DOMAIN = 'domain';
export const TYPE_EMAIL_START = 'emailStart';
export const TYPE_DOMAIN_START = 'domainStart';

export type EmailSearchOptionType = typeof TYPE_DOMAIN | typeof TYPE_EMAIL_START | typeof TYPE_DOMAIN_START;

export type EmailSearchOption = {
  id: EmailSearchOptionType;
  label: ReactNode;
};

// Deliberately loose: this only checks that the term looks reasonably close to
// a domain search (`@gmail`, `@gmail.co.uk`, `@google*`), not that it's a valid
// URL. Capturing groups: 1) domain name ("@gmail"), 2) TLD (".co.uk").
const DOMAIN_PATTERN = /^@([\w*-]+)(\.[\w*-]+(?:\.[\w*-]+)?)?$/;

function splitIntoParts(term: string): [string] | [string, string] {
  const index = term.indexOf('@');
  if (index === -1) return [term];
  return [term.slice(0, index), term.slice(index)];
}

function withAtPrefix(term: string): string {
  return term.includes('@') ? term : `@${term}`;
}

/** The wildcard suggestions offered under the email search input for a typed term. */
export function buildEmailSearchOptions(term: string): EmailSearchOption[] {
  if (isEmptyString(term)) return [];

  const emailStartOption: EmailSearchOption = {
    id: TYPE_EMAIL_START,
    label: (
      <>
        Search for emails that start with <Code>{term}</Code>
      </>
    ),
  };
  const domainMatchOption: EmailSearchOption = {
    id: TYPE_DOMAIN,
    label: (
      <>
        Search for <Code>{withAtPrefix(term)}</Code> domain
      </>
    ),
  };
  const domainStartOption: EmailSearchOption = {
    id: TYPE_DOMAIN_START,
    label: (
      <>
        Search for domains that start with <Code>{withAtPrefix(term)}</Code>
      </>
    ),
  };

  const [id, domain] = splitIntoParts(term);

  if (!isEmptyString(id) && id !== '*') {
    if (domain != null) return [];
    // A + is unlikely to match a domain, so suggest only start-of-email.
    if (id.includes('+')) return [emailStartOption];
    return [emailStartOption, domainStartOption];
  }

  if (domain == null) return [];

  const match = DOMAIN_PATTERN.exec(domain);
  if (match == null) return [];

  const [, name, tld] = match;
  // With both name and TLD (@gmail.com) an exact domain match makes sense;
  // with only the name (@gmail), suggest start-of-domain.
  if (!isEmptyString(name ?? '') && !isEmptyString(tld ?? '')) {
    return [domainMatchOption, domainStartOption];
  }
  return [domainStartOption];
}

/**
 * The final search term for a chosen suggestion: wraps the typed term with
 * the wildcard/domain markers that suggestion implies.
 */
export function buildEmailSearchTerm(term: string, type: EmailSearchOptionType | null): string {
  const prefix = term.startsWith('*') ? '' : '*';
  const domainPrefix = term.startsWith('@') || term.startsWith('*@') ? '' : '@';
  const suffix = term.endsWith('*') ? '' : '*';

  if (type === TYPE_DOMAIN_START) return `${domainPrefix}${term}${suffix}`;
  if (type === TYPE_DOMAIN) return `${prefix}${domainPrefix}${term}`;
  if (type === TYPE_EMAIL_START) return `${term}${suffix}`;
  return term;
}
